import type {
  AgencyDivisionsDraggableListProps,
  ReorderParams,
} from './agency-divisions-draggable-list.interface';
import type { ReactNode } from 'react';
import type { DivisionDto } from 'shared/dtos/divisions.dto';

import {
  closestCenter,
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { bindActionCreators } from '@reduxjs/toolkit';
import { EntityPanel } from 'components/panels/entity-panel/entity-panel';
import { NotFoundMessage } from 'components/template/not-found-message/not-found-message';
import React from 'react';
import { connect } from 'react-redux';
import { updateAgencyThunk } from 'store/agencies/agencies.thunks';

import { usePermissions } from '../../../contexts/permissions.context';
import { vocabulary } from '../../../vocabulary/german';
import { CreateButton } from '../../buttons/create-button/create-button';

const SortableItem = ({
  id,
  children,
  isDisabled,
}: {
  id: number;
  children: ReactNode;
  isDisabled: boolean;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: isDisabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
};

export const AgencyDivisionsListDraggable: React.FC<
  AgencyDivisionsDraggableListProps
> = ({ agency, ...rest }) => {
  const matrix = usePermissions();
  const isTouchDevice =
    typeof window !== 'undefined' && 'ontouchstart' in window;
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 8 },
  });

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { delay: 200, tolerance: 5 },
  });

  const sensors = useSensors(isTouchDevice ? touchSensor : pointerSensor);

  const [state, setState] = React.useState<{ divisions: DivisionDto[] }>({
    divisions: [...(agency.Divisions || [])],
  });

  const byDivisionOrder = (a, b) =>
    a.orderInAgencyDivisionsList - b.orderInAgencyDivisionsList;

  const renderCreateDivisionsBtnAgency = (item): JSX.Element | undefined => {
    if (matrix.permissions.createNew === true) {
      return (
        <CreateButton
          text={vocabulary.buttons.createNewDivision}
          onClick={() => rest.handleSelectAgency(item)}
        />
      );
    }
  };

  const onDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = state.divisions.findIndex(
        (item) => item.id === active.id,
      );
      const newIndex = state.divisions.findIndex((item) => item.id === over.id);

      const sourceId = state.divisions[oldIndex].id;
      const newPosition = newIndex;
      const params = {
        agencyId: agency.id,
        divisionId: sourceId,
        orderInAgencyDivisionsList: newPosition,
      } as ReorderParams;

      rest.handleReorderCurrentAgencyDivisions(params);

      setState((prevState) => {
        return {
          divisions: arrayMove(prevState.divisions, oldIndex, newIndex).map(
            (division, ind) => ({
              ...division,
              orderInAgencyDivisionsList: ind,
            }),
          ),
        };
      });
    }
  };

  if (state.divisions.length === 0) {
    return (
      <React.Fragment>
        <NotFoundMessage
          message={`Nicht gefunden: Ambitionen konnten nicht gefunden werden`}
        />
        {renderCreateDivisionsBtnAgency(agency)}
      </React.Fragment>
    );
  }

  const divisions = (state.divisions || []).sort(byDivisionOrder);

  const divisionsIdsForContext = divisions.map(
    (division) => division.id as number,
  );

  const isDraggingDisabled =
    matrix.permissions.dragFields === 'empty' ||
    matrix.permissions.dragFields === false;

  return (
    <React.Fragment>
      <div className={`agency-divisions-draggable-list-container`}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          {' '}
          <SortableContext
            items={divisionsIdsForContext}
            strategy={verticalListSortingStrategy}
          >
            <div className={'entities-list'}>
              {divisions.map((division, divisionIndex) => (
                <SortableItem
                  key={division.id}
                  id={division.id as number}
                  isDisabled={isDraggingDisabled}
                >
                  <EntityPanel
                    key={divisionIndex}
                    onClick={() => rest.handleSelect(division)}
                    title={division.title}
                    id={division.id}
                  />
                </SortableItem>
              ))}
              {renderCreateDivisionsBtnAgency(agency)}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </React.Fragment>
  );
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ updateAgencyThunk }, dispatch);
export default connect(null, mapDispatchToProps)(AgencyDivisionsListDraggable);
